/* eslint-disable @typescript-eslint/member-ordering */

import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  EMPTY,
  catchError,
  defer,
  expand,
  lastValueFrom,
  Observable,
  of,
  first,
  from,
  map,
  reduce,
  switchMap,
  withLatestFrom,
  throwError,
} from 'rxjs';

import { GENERIC_CSV_INTEGRATION_CLASS } from '@entities/platform-entities/integration/generic-csv-integration-class.const';
import { Integration } from '@entities/platform-entities/integration/integration.entity';
import { SFCC_INTEGRATION_CLASS } from '@entities/platform-entities/integration/sfcc-integration-class.const';
import { StoreAllowedDomain } from '@entities/platform-entities/store/interfaces/store-allowed-domain.interface';
import { Store } from '@entities/platform-entities/store/store.entity';

import { PlatformShopifyAppService } from '../api/platform/services/platform-shopify-app.service';
import { PlatformStoresService } from '../api/platform/services/platform-stores.service';

import {
  CreateAllowedDomainDto,
  CreateIntegrationDto,
  CreateStoreDto,
  StoreDto,
  UpdateStoreDto,
} from '../api/platform/models';
import { ProductsServiceProductsService } from '../api/products-service';

import {
  DbIntegrationModel,
  SHOPIFY_INTEGRATION_CLASSES,
} from '../../../../shared/db-models/integration';
import { DbCurrencyModel } from '../../../../shared/db-models/payments';
import { DbSessionModel } from '../../../../shared/db-models/session';

@Injectable({
  providedIn: 'root',
})
export class StoresService {
  private injector: Injector;
  private firestore: AngularFirestore;
  private platformStoresService: PlatformStoresService;
  private platformShopifyAppService: PlatformShopifyAppService;
  private productsServiceApi: ProductsServiceProductsService;
  constructor() {
    this.injector = inject(Injector);
    this.firestore = inject(AngularFirestore);
    this.platformStoresService = inject(PlatformStoresService);
    this.platformShopifyAppService = inject(PlatformShopifyAppService);
    this.productsServiceApi = inject(ProductsServiceProductsService);
  }

  // region Stores

  public getAllStores(): Observable<StoreDto[]> {
    const size = 5000;
    return this.platformStoresService
      .storeControllerFindMany({ order: 'asc', orderBy: 'name', page: 1, size })
      .pipe(
        expand((response, index) => {
          if (!response.hasMore) return EMPTY;
          return this.platformStoresService.storeControllerFindMany({
            order: 'asc',
            orderBy: 'name',
            page: index + 2,
            size,
          });
        }),
        map((response) => (response.data ?? []) as unknown as StoreDto[]),
        reduce((all, pageItems) => [...all, ...pageItems], [] as StoreDto[])
      );
  }

  public deleteStore(id: string): Observable<StoreDto> {
    return this.platformStoresService.storeControllerDelete({ storeId: id });
  }

  public getStoreById(id: string): Observable<StoreDto | null> {
    return this.platformStoresService
      .storeControllerFindById({ storeId: id })
      .pipe(
        catchError((err: unknown) => {
          // "Not found" is a valid outcome for existence checks.
          if (err instanceof HttpErrorResponse && err.status === 404) return of(null);
          return throwError(() => err);
        })
      )
      .pipe(map((response) => response as unknown as StoreDto));
  }

  public async storeExists(id: string): Promise<boolean> {
    const doc = await lastValueFrom(this.getStoreById(id));
    return !!doc;
  }

  public getSessions() {
    const stores = this.getAllStores();
    // Use compat API within injection context to avoid injection errors
    return runInInjectionContext(this.injector, () => {
      return this.firestore
        .collection<DbSessionModel>(`sessions`)
        .get()
        .pipe(
          map((query) => query.docs),
          map((docs) => docs.map((doc) => doc.data())),
          withLatestFrom(stores),
          map(([sessions, stores]) =>
            sessions.map((session) => ({
              ...session,
              // eslint-disable-next-line sonarjs/no-nested-functions
              storeName: stores.find((store) => store.id === session.storeId)?.name,
            }))
          ),
          map((sessions) =>
            sessions.map((session) => ({
              id: session.id,
              name: `${session.storeName} - ${session.hostName} - ${session.startTime
                .toDate()
                .toLocaleString()}`,
            }))
          ),
          first()
        );
    });
  }

  public getShopifyStoreIdsThatHaveIntegration(): Observable<string[]> {
    // Use compat API within injection context to avoid injection errors
    return runInInjectionContext(this.injector, () => {
      const collectionRef = this.firestore
        .collection('integrations')
        .ref.where('integrationClass', 'in', [...SHOPIFY_INTEGRATION_CLASSES]);
      return from(collectionRef.get()).pipe(
        map((querySnapshot) => {
          const allStoreIds = new Set<string>();
          for (const doc of querySnapshot.docs) {
            const storeId = (<DbIntegrationModel>doc.data()).storeId;
            if (storeId) {
              allStoreIds.add(storeId);
            }
          }
          return [...allStoreIds];
        })
      );
    });
  }

  public hasShopifyAppInstalled(storeId: string): Observable<boolean> {
    if (!storeId) {
      return of(false);
    }

    return this.platformShopifyAppService
      .shopifyAppControllerGet({ 'terrific-store-id': storeId })
      .pipe(
        map(() => true),
        catchError((err: unknown) => {
          // "Not found" means the store has no Shopify app session.
          if (err instanceof HttpErrorResponse && err.status === 404) return of(false);
          return throwError(() => err);
        })
      );
  }

  private buildAllowedDomainsPayload(
    allowedDomains: StoreAllowedDomain[] = []
  ): CreateAllowedDomainDto[] {
    return allowedDomains.map((allowedDomain) => ({
      addressPattern: allowedDomain.addressPattern,
      createdAt: this.serializeDate(allowedDomain.createdAt),
      createdBy: allowedDomain.createdBy,
      id: allowedDomain.id,
      isEnabled: allowedDomain.isEnabled,
      updatedAt: this.serializeDate(allowedDomain.updatedAt),
      updatedBy: allowedDomain.updatedBy,
    }));
  }

  private serializeDate(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
    }
    // Firestore Timestamp-like object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (value as any)?.toDate === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const date = (value as any).toDate();
      return date instanceof Date ? date.toISOString() : undefined;
    }
    return undefined;
  }

  private buildIntegrationDto(integration: Integration): CreateIntegrationDto {
    return {
      EmbeddingFontFamily: integration.EmbeddingFontFamily,
      apiToken: integration.apiToken,
      apiUrl: integration.apiUrl,
      cartPath: integration.cartPath,
      credentials: integration.credentials,
      cronScheduledSync: integration.cronScheduledSync,
      cronScheduledSyncTimezone: integration.cronScheduledSyncTimezone,
      csvIntegrationConfig:
        integration.integrationClass === GENERIC_CSV_INTEGRATION_CLASS
          ? integration.csvIntegrationConfig
          : undefined,
      customerStoreId: integration.customerStoreId,
      enableAllProducts: integration.enableAllProducts,
      extraProductIds: integration.extraProductIds,
      headerElements: integration.headerElements,
      integrationClass: integration.integrationClass,
      productCatalogUrl: integration.productCatalogUrl,
      productFlags: integration.productFlags,
      productIds: integration.productIds,
      productUniqueIdFieldName: integration.productUniqueIdFieldName,
      productVariantUniqueIdFieldName: integration.productVariantUniqueIdFieldName,
      sfccIntegrationConfig:
        integration.integrationClass === SFCC_INTEGRATION_CLASS
          ? integration.sfccIntegrationConfig
          : undefined,
      websiteUrl: integration.websiteUrl,
    };
  }

  private buildStorePayload(
    store: Omit<Store, 'id'> & { integration?: Integration }
  ): CreateStoreDto | UpdateStoreDto {
    return {
      allowedDomains: this.buildAllowedDomainsPayload(
        store.allowedDomains
      ) as unknown as CreateStoreDto['allowedDomains'],
      bannerUrl: store.bannerUrl,
      broadcastResolution: store.broadcastResolution,
      cartQuantityControlsSupported: store.cartQuantityControlsSupported,
      checkoutRedirectRoute: store.checkoutRedirectRoute,
      colorPalateSelector: store.colorPalateSelector,
      currency: store.currency,
      externalCart: store.externalCart,
      externalUrl: store.externalUrl,
      featureFlags: store.featureFlags as unknown as CreateStoreDto['featureFlags'],
      hostVideoGrid: store.hostVideoGrid,
      industry: store.industry,
      integration: this.buildIntegrationDto(
        (store.integration as unknown as Integration) || new Integration()
      ),
      ipAllowlist: store.ipAllowlist,
      isAutoCheckoutEnabled: store.isAutoCheckoutEnabled,
      isPublished: store.isPublished,
      leadImagePosition: store.leadImagePosition,
      logoUrl: store.logoUrl,
      maximumTimelineVideoLength: store.maximumTimelineVideoLength,
      name: store.name,
      paymentSupplier: store.paymentSupplier as unknown as CreateStoreDto['paymentSupplier'],
      pictureInPicture: store.pictureInPicture,
      productsLogoUrl: store.productsLogoUrl,
      socialImportsMaxItemsPerImport: store.socialImportsMaxItemsPerImport,
      socialImportsMaxOnDemandRequestsDaily: store.socialImportsMaxOnDemandRequestsDaily,
      storeCategory: store.storeCategory,
      storeConfig: store.storeConfig as unknown as CreateStoreDto['storeConfig'],
      storeType: store.storeType,
      theme: store.theme as unknown as CreateStoreDto['theme'],
      url: store.url,
    };
  }

  /**
   * Creates or updates a store and returns it's id
   * @param store The store object
   */
  public updateStore({ id, ...store }: Store, isEdit: boolean) {
    return defer(() => {
      const request$ = isEdit
        ? this.platformStoresService.storeControllerUpdate({
            body: this.buildStorePayload(store) as UpdateStoreDto,
            storeId: id,
          })
        : this.platformStoresService.storeControllerCreate({
            body: this.buildStorePayload(store) as CreateStoreDto,
          });

      return request$.pipe(
        switchMap(async (storeDto) => {
          const storeId = isEdit ? id : storeDto?.id;
          if (!storeId) return null;
          if (isEdit) return storeId;

          const externalUrl = storeDto?.externalUrl ?? store.externalUrl;
          if (!externalUrl) return storeId;

          const hostName = new URL(externalUrl).hostname;
          const hasShopifyHandle = await fetch(`https://${hostName}/products.json?limit=1`)
            .then((response) => response.ok)
            .catch(() => false);
          if (!hasShopifyHandle) return storeId;

          await this.syncIntegrationData(storeId);

          return storeId;
        })
      );
    });
  }

  private trimSlashes(str: string): string {
    // eslint-disable-next-line sonarjs/slow-regex
    return str ? str.replaceAll(/^\/+|\/+$/g, '') : '';
  }

  public cleanIntegrationData(integration: Integration) {
    integration.apiUrl = this.trimSlashes(integration.apiUrl);
    integration.websiteUrl = this.trimSlashes(integration.websiteUrl);
    integration.cartPath = this.trimSlashes(integration.cartPath);
    integration.productCatalogUrl = this.trimSlashes(integration.productCatalogUrl);
  }

  public async syncIntegrationData(storeId: string): Promise<void> {
    await lastValueFrom(
      this.productsServiceApi.productsControllerSync({
        body: {},
        'terrific-store-id': storeId,
      })
    );
  }

  // endregion

  // region Currencies

  public getAllCurrencies() {
    // Use compat API within injection context to avoid injection errors
    return runInInjectionContext(this.injector, () => {
      return this.firestore
        .collection<DbCurrencyModel>('currencies', (ref) => ref.where('code', '!=', null))
        .get();
    });
  }

  // endregion
}
